import { Component, Element, h, Host, Prop, State } from '@stencil/core';
import { ApiService } from '../../service/api-service';
import { Sanitizer } from '../../utils/sanitizer';
import i18next, { i18nInit } from '../../i18n';

type Star = {
  id: number;
  checked: boolean;
};

@Component({
  tag: 'ba-feedback',
  styleUrl: 'ba-feedback.scss',
  shadow: true,
})
export class BaFeedback {
  /**
   * A custom descriptor used to define the client's use case
   */
  @Prop() subject: string;

  /**
   * The id you receive when registering your feedback usage. The clientId determines the receiver email of user feedback.
   */
  @Prop() clientId: string;

  /**
   * The additional information you decide to add when sending the feedback message.
   */
  @Prop() additionalInformation?: string;

  @Element() element: HTMLBaFeedbackElement;

  @State() ratingDone: boolean;
  @State() formSent: boolean;
  @State() serverError: boolean;
  @State() submitting: boolean;
  @State() spinner: boolean;
  @State() votedStars: string;
  @State() showMicrocopy: boolean;
  @State() showWarning: boolean;
  private showError: boolean;
  private validationConfigRequestSent: boolean;
  private stars: Star[];
  private selectedRating: string;
  private messageCall: string;
  private ratingAria: string;
  private ofLabel: string;
  private starsLabel: string;
  private noStarSelectedText: string;
  private singleStarSelectedText: string;
  private multiStarsSelectedText: string;
  private sendButtonText: string;
  private confirmationMessage: string;
  private privacyDisclaimer: string;
  private errorText: string;
  private apiService: ApiService;
  private sanitizer: Sanitizer;
  private dpl: object;
  private oidcPublicClientId: string;
  private messageMaxLength: number;
  private maxLengthTextMessage: string;
  private maxLengthTextWarning: string;
  private messageHeaderConfirm: string;
  private messageHeaderError: string;
  private errorConfigFirst: string;
  private errorConfigSecond: string;

  constructor() {
    this.stars = [
      { id: 1, checked: false },
      { id: 2, checked: false },
      { id: 3, checked: false },
      { id: 4, checked: false },
      { id: 5, checked: false },
    ];
    this.ratingDone = false;
    this.formSent = false;
    this.selectedRating = '-1';
    this.submitting = false;
    this.apiService = new ApiService();
    this.sanitizer = new Sanitizer();
    this.spinner = false;
    this.votedStars = '0';
    this.messageMaxLength = 10000;
    this.showWarning = false;
    this.showError = false;
    this.validationConfigRequestSent = false;
    window.addEventListener('oiamTokenEvent', this.onOiamTokenReceive.bind(this));
    window.self.dispatchEvent(new CustomEvent('oiamSendTokenEvent'));
    this.handleFocus();
  }

  async componentWillLoad() {
    const language = document.documentElement.getAttribute('lang') || 'de';
    await i18nInit(language);
    this.messageCall = i18next.t('message.cta');
    this.ratingAria = i18next.t('rating.aria');
    this.ofLabel = i18next.t('rating.aria.of');
    this.starsLabel = i18next.t('rating.aria.stars');
    this.noStarSelectedText = i18next.t('rating.aria.nostarselected');
    this.singleStarSelectedText = i18next.t('rating.aria.singlestarselected');
    this.multiStarsSelectedText = i18next.t('rating.aria.multistarsselected');
    this.sendButtonText = i18next.t('button.send');
    this.confirmationMessage = i18next.t('message.confirm');
    this.privacyDisclaimer = i18next.t('privacy.disclaimer');
    this.errorText = i18next.t('error.text');
    this.maxLengthTextMessage = i18next.t('maxlength.message');
    this.maxLengthTextWarning = i18next.t('maxlength.messagewarning');
    this.messageHeaderConfirm = i18next.t('message.header.confirm');
    this.messageHeaderError = i18next.t('message.header.error');
    this.errorConfigFirst = i18next.t('error.config.first');
    this.errorConfigSecond = i18next.t('error.config.second');
  }

  componentDidLoad() {
    this.dpl = window['dpl'];
  }

  render() {
    if (this.showError) {
      return (
        <Host>
          <div id="error-message" class="alert alert-error" role="alert">
            <span class="sr-only">{this.messageHeaderError}</span>
            <p>
              {this.errorConfigFirst}
              <br />
              {this.errorConfigSecond}
            </p>
          </div>
        </Host>
      );
    }
    return (
      <Host>
        {this.formSent ? (
          <div id="success-message" class="alert" role="alert">
            <p class="ba-heading h4 weight-700">{this.messageHeaderConfirm}</p>
            <p>{this.confirmationMessage}</p>
          </div>
        ) : (
          <div id="main-ba-feedback" class="ba-keyboard-mode">
            <form name="form" action="[[endpoint]]" method="post" onSubmit={this.submitForm} autocomplete="on" novalidate>
              <div id="slot-title" class="h6 text-center hoverable" onMouseOver={this.handleSlotTitleHover} onFocus={this.handleSlotTitleHover}>
                <slot name="header" />
                <span id="rating-instructions" class="sr-only" aria-live="polite">
                  {this.ratingAria}
                </span>
              </div>
              <div class="star-rating alignable center">
                <fieldset aria-labelledby="slot-title">
                  <div class="row justify-content-center">
                    {this.stars.map((star) => (
                      <div key={'star-' + star.id} class="col-auto">
                        <div key={star.id} class="row justify-content-center mb-3 star hoverable alignable">
                          <button
                            class="ba-btn ba-btn-ghost ba-btn-icon-only ba-icon-evaluation-empty"
                            type="button"
                            data-value={star.id}
                            name="rating"
                            id={'rating' + star.id}
                            aria-label={this.getTitle(star.id)}
                            onClick={this.forwardToRadioInput}
                            onMouseOver={this.fillStarsBasedOnHoveredStar}
                            onMouseOut={this.fillStarsBasedOnHoveredStar}
                            onFocus={this.fillStarsBasedOnHoveredStar}
                            onBlur={this.fillStarsBasedOnHoveredStar}></button>
                        </div>
                      </div>
                    ))}
                  </div>
                </fieldset>
              </div>
              {this.getMessageContainer()}
            </form>
          </div>
        )}
      </Host>
    );
  }

  private handleSlotTitleHover = () => {
    const ratingInstructionsElement = this.element.shadowRoot?.querySelector('#rating-instructions');

    if (ratingInstructionsElement) {
      const currentText = this.ratingAria;
      ratingInstructionsElement.textContent = '';
      setTimeout(() => {
        ratingInstructionsElement.textContent = currentText;
      }, 50);
    }
  };

  private getMessageContainer() {
    if (this.ratingDone) {
      return (
        <fieldset class="message-container">
          <label id="feedback-message-label">
            {this.messageCall}
            <p class="sr-only">{this.privacyDisclaimer}</p>
            <textarea
              id="feedback-message"
              class={this.showWarning ? 'form-control mt-1 ba-warning' : 'form-control mt-1'}
              name="text"
              aria-describedby={this.showWarning ? 'microcopy-for-feedback-message-warning' : 'microcopy-for-feedback-message'}
              maxlength={this.messageMaxLength}
              onInput={this.handleMessageLength}></textarea>
          </label>
          {this.showWarning ? (
            <p id="microcopy-for-feedback-message-warning" class="form-text ba-warning" aria-live="polite">
              {this.maxLengthTextWarning}
            </p>
          ) : (
            <p id="microcopy-for-feedback-message" aria-hidden="true" class="form-text">
              {this.maxLengthTextMessage}
            </p>
          )}
          <p id="disclaimer" class="space-l justify mt-3 feedback-component-text">
            {this.privacyDisclaimer}
          </p>
          <div class="flex-container mt-3 text-end">
            <button id="feedback-send-button" type="submit" aria-label={this.sendButtonText} class="ba-btn ba-btn-primary flex-centerable">
              <span class="text-overflow">{this.sendButtonText}</span>
            </button>
          </div>
          {this.getServerErrorMessage()}
          {this.renderSpinner(this.spinner)}
        </fieldset>
      );
    }
  }

  private getTitle(starId: number) {
    const titleText = 'Position ' + starId + ' ' + this.ofLabel + ' ' + this.stars.length + ' ' + this.starsLabel;
    if (this.votedStars === '0') {
      return `${titleText} ${this.noStarSelectedText}`;
    }
    if (this.votedStars === '1') {
      return `${titleText} ${this.singleStarSelectedText}`;
    }
    return `${titleText} ${this.votedStars} ${this.multiStarsSelectedText}`;
  }
  private renderSpinner(spinner) {
    if (spinner) {
      return <div class="ba-loading" id="feedback-spinner"></div>;
    }
  }

  private getServerErrorMessage() {
    if (this.serverError) {
      return (
        <div id="error-message" class="alert alert-error mt-3" role="alert">
          <span class="sr-only">{this.messageHeaderError}</span>
          <p>{this.errorText}</p>
        </div>
      );
    } else {
      return;
    }
  }

  private refreshStars() {
    const selected = parseInt(this.selectedRating);
    for (let i = 0; i < 5; i++) {
      const currentStar = this.getStarButton(i + 1);
      if (i < selected) {
        currentStar.classList.add('ba-icon-evaluation-full');
      } else {
        currentStar.classList.remove('ba-icon-evaluation-full');
      }
    }
  }
  private fillStarsBasedOnHoveredStar = (event) => {
    const hoveredStar: number = parseInt(this.getStarId(event));
    const selectedRating = parseInt(this.selectedRating);
    if (selectedRating > 0 && selectedRating >= hoveredStar) {
      return;
    }
    const index = selectedRating > 0 ? selectedRating : 0;
    let i = index;
    for (i; i < hoveredStar; i++) {
      const currentStar = this.getStarButton(i + 1);
      currentStar.classList.toggle('ba-icon-evaluation-full');
    }
  };

  private selectStar(inputElement) {
    this.ratingDone = true;
    this.selectedRating = inputElement.dataset.value || '-1';
    this.refreshStars();
  }
  private forwardToRadioInput = (event) => {
    const starRating = this.getStarId(event);
    this.votedStars = starRating;
    const input = this.element.shadowRoot?.querySelector(`button#rating${starRating}`);
    this.selectStar(input);
    return input;
  };

  private getStarId(event): string {
    const replaceText = event.currentTarget.tagName === 'BUTTON' ? 'rating' : 'star-label-new-';
    return event.currentTarget.id.replace(replaceText, '');
  }

  private getStarButton(inputId): HTMLButtonElement {
    const buttonIcon = this.element.shadowRoot?.querySelector(`button#rating${inputId}`) as HTMLButtonElement;
    if (buttonIcon === null) {
      throw new Error(`Button ${inputId} not found. This is unexpected.`);
    }
    return buttonIcon;
  }

  private handleFocus = () => {
    const baFeedback = this.element;
    baFeedback.classList.add('shadow-none');
  };

  private handleMessageLength = (event) => {
    const messageLength = event.currentTarget.value.length;
    this.showWarning = messageLength >= this.messageMaxLength;
    return this.showWarning;
  };

  private onOiamTokenReceive(e) {
    this.oidcPublicClientId = e.detail.clientid;
    if (!this.validationConfigRequestSent) {
      this.validateFeedbackConfig().then(() => {});
    }
  }

  private sanitizeAdditionalInformation(additionalInformation?: string) {
    if (additionalInformation === undefined) {
      return;
    }
    return Object.fromEntries(
      Object.entries(JSON.parse(additionalInformation)).map(([key, value]) => [this.sanitizer.sanitize(key), this.sanitizer.sanitize(value)])
    );
  }

  private async sendForm(sanitizedInput: string) {
    let response: Response;
    if (this.oidcPublicClientId !== undefined) {
      response = await this.apiService.sendFormWithOidc(
        this.clientId,
        sanitizedInput,
        this.subject,
        this.selectedRating,
        this.oidcPublicClientId,
        this.sanitizeAdditionalInformation(this.additionalInformation)
      );
    } else {
      response = await this.apiService.sendFormWithoutOidc(
        this.clientId,
        sanitizedInput,
        this.subject,
        this.selectedRating,
        this.sanitizeAdditionalInformation(this.additionalInformation)
      );
    }
    return response.status;
  }

  private submitForm = async (event) => {
    event.preventDefault();
    this.spinner = true;
    const userInput = (this.element.shadowRoot?.querySelector('#feedback-message') as HTMLTextAreaElement).value;
    const sanitizedInput = this.sanitizer.sanitize(userInput);
    try {
      const responseStatus = await this.sendForm(sanitizedInput);
      if (responseStatus === 200) {
        this.formSent = true;
      } else {
        this.serverError = true;
      }
    } catch {
      this.serverError = true;
    }
    this.spinner = false;
  };

  private async validateFeedbackConfig() {
    this.validationConfigRequestSent = true;
    const encodeAddInfos = new TextEncoder();
    this.spinner = true;
    try {
      const response = await this.apiService.validateFeedbackConfig(
        this.clientId,
        this.subject,
        this.oidcPublicClientId,
        Array.from(encodeAddInfos.encode(this.additionalInformation))
      );
      this.spinner = false;
      this.showError = response.status === 400;
    } catch {
      this.spinner = false;
    }
  }
}
